import React, { FunctionComponent, useMemo } from 'react';
import Benefits, { BenefitProps } from '@/components/Benefits/Benefits';
import { Layout } from '@/components/Layout';
import TestimonialsCTA from '@/components/Testimonials/TestimonialsCTA';
import WhyVisalexAdvantages from '@/components/WhyVisalex/WhyVisalexAdvantages';
import { Col, ComponentWithProps, Container, createArrayInRange, Hero, Row } from '@visalex/core';

import cx from 'clsx';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import styles from '@/components/WhyVisalex/WhyVisalex.module.scss';

export const NarrowSection: ComponentWithProps = ({ className, children }) => (
  <section className={className} data-testid="narrow-section">
    <Container>
      <Row>
        <Col lg={10} className="mx-auto">
          {children}
        </Col>
      </Row>
    </Container>
  </section>
);

const prefix = 'why-visalex';

const WhyVisalex: FunctionComponent = () => {
  const { t, i18n } = useTranslation();
  const benefits: BenefitProps[] = useMemo(
    () =>
      createArrayInRange(0, 5).map((index) => ({
        key: index,
        description: t(`${prefix}.benefits.${index}`),
        imageName: `why-we-help-benefit-${index + 1}.svg`,
      })),
    [i18n.language]
  );
  const features: BenefitProps[] = useMemo(
    () =>
      createArrayInRange(0, 5).map((index) => ({
        key: index,
        description: t(`${prefix}.features.${index}`),
        imageName: `why-we-help-feature-${index + 1}.svg`,
      })),
    [i18n.language]
  );

  return (
    <Layout title={t(`${prefix}.seo-title`)} description={t(`${prefix}.seo-description`)}>
      <div className={cx(styles[`${prefix}`])}>
        <Hero className={styles[`${prefix}__hero`]}>
          <Col xs={12} lg={10} className="mx-auto">
            <h1>{t(`${prefix}.hero.title`)}</h1>
            <p dangerouslySetInnerHTML={{ __html: t(`${prefix}.hero.subtitle`) }}></p>
          </Col>
        </Hero>
        <WhyVisalexAdvantages />
        <NarrowSection className={styles[`${prefix}__benefits`]}>
          <h2 className="text-center w-100">{t(`${prefix}.more-than`)}</h2>
          <h5 className="text-center mb-5">{t(`${prefix}.key-benefits`)}</h5>
          <Row>
            <Benefits benefits={benefits} colProps={{ xs: 6, lg: 4, xl: { span: 3, offset: 1 } }} />
          </Row>
        </NarrowSection>
        <NarrowSection className={styles[`${prefix}__benefits`]}>
          <h5 className="text-center mb-5">{t(`${prefix}.key-features`)}</h5>
          <Row>
            <Benefits benefits={features} colProps={{ xs: 6, lg: 4, xl: { span: 3, offset: 1 } }} />
          </Row>
        </NarrowSection>
        <NarrowSection className={styles[`${prefix}__fact-sheet`]}>
          <Row>
            <Col xs={12} md={6} lg={{ span: 3, offset: 2 }}>
              <img
                src={`/images/fact_sheet-preview-${i18n.language}.png`}
                alt={t(`${prefix}.fact-sheet`)}
                className="w-100"
              />
            </Col>
            <Col xs={12} md={6} lg={{ span: 4, offset: 1 }} className="d-flex">
              <div className="my-auto">
                <p>{t(`${prefix}.fact-sheet-info`)}</p>
                <a
                  download
                  href={`/pdf/Visalex_Fact_Sheet_${i18n.language}.pdf?v=2`}
                  className="d-flex align-items-center"
                >
                  <img src="/icons/icon-pdf.svg" alt="PDF" />
                  <span>{t(`${prefix}.fact-sheet-link`)}</span>
                </a>
              </div>
            </Col>
          </Row>
        </NarrowSection>
        <NarrowSection className={styles[`${prefix}__CTA`]}>
          <TestimonialsCTA header={t(`${prefix}.CTA`)} subtitle={t(`${prefix}.CTA-subtitle`)} />
        </NarrowSection>
      </div>
    </Layout>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}

export default WhyVisalex;
